import type {
  ProductsQuery,
  ProductVariant,
  BasePrice,
  BaseSavedPrice,
  Maybe,
} from '../graphql/v2';
import { isTruthy } from './predicate';

export type ProductInterface = NonNullable<
  NonNullable<ProductsQuery['products']>[0]
>;

export function getVariant(product: ProductInterface) {
  const isConfigurableProduct = product.__typename === 'ConfigurableProduct';

  const loyaltyVariant =
    isConfigurableProduct && product.loyaltyVariant
      ? product.loyaltyVariant
      : null;

  const defaultVariant = isConfigurableProduct ? product.defaultVariant : null;

  return loyaltyVariant || defaultVariant || null;
}

export function getLoyaltyVariant(product: ProductInterface) {
  const isConfigurableProduct = product.__typename === 'ConfigurableProduct';

  const loyaltyVariant =
    isConfigurableProduct && product.loyaltyVariant
      ? product.loyaltyVariant
      : null;

  return loyaltyVariant;
}

type Props = { variant?: ProductVariant | null; product: ProductInterface };

export function getDiscountPrices({ variant, product }: Props) {
  const bundleProduct = product.__typename === 'BundleProduct';
  const configurableProduct = product.__typename === 'ConfigurableProduct';
  const isLoyaltyVariant = isTruthy(getLoyaltyVariant(product));

  let previousPrice: Maybe<BasePrice> | undefined;
  let savingsPrice: Maybe<BaseSavedPrice> | undefined;
  let previousPriceMessage:
    | 'regularPrice'
    | 'fullVersionPrice'
    | 'totalPriceSeparately'
    | 'regularUpdatePrice'
    | undefined;

  const isVariantOnSale = isTruthy(variant?.onSale);
  const isProductOnSale = isTruthy(product?.onSale);

  if (
    (isVariantOnSale && !isLoyaltyVariant) ||
    (bundleProduct && isProductOnSale)
  ) {
    previousPrice = variant?.price?.regular;
    savingsPrice = variant?.price?.savings?.regularToFinal;
    previousPriceMessage = 'regularPrice';
  }

  if (configurableProduct && isVariantOnSale && isLoyaltyVariant) {
    previousPrice = variant?.price?.regular;
    savingsPrice = variant?.price?.savings?.regularToFinal;
    previousPriceMessage = 'regularUpdatePrice';
  }

  if (configurableProduct && isLoyaltyVariant && !isVariantOnSale) {
    previousPrice = product?.price?.regular;
    savingsPrice = product?.price?.savings?.regularToLoyalty;
    previousPriceMessage = 'fullVersionPrice';
  }

  if (
    (configurableProduct && !isLoyaltyVariant && !isVariantOnSale) ||
    (bundleProduct && !isProductOnSale)
  ) {
    previousPrice = product?.price?.totalBundle;
    savingsPrice = product?.price?.savings?.totalBundleToFinal;
    previousPriceMessage = 'totalPriceSeparately';
  }

  return { previousPrice, savingsPrice, previousPriceMessage };
}

export function getReleaseDate({
  product,
  variant,
}: {
  product: ProductInterface;
  variant: ProductVariant | undefined | null;
}) {
  if (isTruthy(product.releaseDate) && !isTruthy(variant)) {
    return new Date(product.releaseDate);
  }

  if (variant && isTruthy(variant?.releaseDate)) {
    return new Date(variant.releaseDate);
  }

  return null;
}

export function getStockStatus({ product, variant }: Props): string {
  // We only consider stockStatus property from variants and BundleProducts.
  const isBundleProduct = product.__typename === 'BundleProduct';

  if (!variant && isBundleProduct) {
    return product.stockStatus;
  }

  if (variant) {
    return variant.stockStatus;
  }

  return 'IN_STOCK';
}

export function getIsShippable({ product, variant }: Props): boolean {
  // Only variants are shippable together with products with BundleProduct type.
  // SimpleProducts/VirtualProducts are part of ConfigurableProducts, so we
  // handle them through variants (defaultVariant).
  const isProductTypeShippable = product.__typename === 'BundleProduct';

  if (!variant && isProductTypeShippable) {
    return product.shippable;
  }

  if (variant) {
    return variant?.shippable;
  }

  return false;
}
